import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Clock, ArrowLeft, Loader2, Mail } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { api } from "@/lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/blog/${slug}`)
      .then((res) => {
        setPost(res.data);
        setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin text-warm" />
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="grain relative min-h-screen">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 pt-40 pb-24 text-center relative z-10">
          <h1 className="font-heading font-bold text-3xl mb-4">Article introuvable</h1>
          <Link to="/blog" className="text-accent hover:underline">Retour au blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.meta_description,
    image: post.cover_image,
    author: { "@type": "Organization", name: post.author },
    publisher: { "@type": "Organization", name: "GMP Développement" },
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    mainEntityOfPage: `${origin}/blog/${post.slug}`,
    keywords: post.tags.join(", "),
  };

  return (
    <div className="grain relative min-h-screen">
      <Helmet>
        <title>{`${post.title} | GMP Développement`}</title>
        <meta name="description" content={post.meta_description} />
        <meta name="keywords" content={post.tags.join(", ")} />
        <link rel="canonical" href={`${origin}/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description} />
        <meta property="og:image" content={post.cover_image} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Navbar />

      <article className="max-w-3xl mx-auto px-6 lg:px-8 pt-32 pb-24 relative z-10">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-warm mb-8 transition-colors">
          <ArrowLeft size={16} /> Tous les articles
        </Link>

        <div className="flex items-center gap-3 mb-5 flex-wrap">
          {post.tags.map((t) => (
            <span key={t} className="font-mono text-[9px] tracking-wider uppercase text-warm border border-warm/30 px-2 py-0.5">
              {t}
            </span>
          ))}
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock size={12} /> {post.reading_time} min de lecture
          </span>
        </div>

        <h1 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">
          {post.title}
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed mb-8">{post.excerpt}</p>

        <div className="border border-border overflow-hidden mb-10">
          <img src={post.cover_image} alt={post.title} className="w-full max-h-[440px] object-cover" />
        </div>

        <div className="prose-gmp" data-testid="blog-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>

        <div className="mt-14 border border-warm/30 bg-warm/5 p-8 text-center">
          <h3 className="font-heading font-semibold text-2xl mb-3">Envie de booster vos avis Google ?</h3>
          <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
            Parlons de votre enseigne. On construit une solution sur mesure, humaine et efficace.
          </p>
          <a
            href="/#contact"
            className="inline-flex items-center gap-2 bg-warm text-background font-semibold px-6 py-3 hover:bg-warm-soft transition-colors"
          >
            <Mail size={18} /> Prendre contact
          </a>
        </div>
      </article>
      <Footer />
    </div>
  );
}
